import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "lib/trpc";
import { Reactions } from "screens/social/Reactions";
import { ClientCurrentUser, ClientPost, ClientReactionType } from "@compass/shared/types";

type SocialPostMetaProps = {
    post: ClientPost;
    currentUser?: ClientCurrentUser;
};

export const SocialPostMeta = ({ post, currentUser }: SocialPostMetaProps) => {
    const queryClient = trpc.useContext();
    const rawQueryClient = useQueryClient();

    const togglePostReactionMutation = trpc.social.posts.react.useMutation({
        onSuccess: () => {
            queryClient.social.posts.latest.invalidate();
        },
    });

    const toggleReaction = (reaction: ClientReactionType) => {
        if (togglePostReactionMutation.isPending) return;

        // Optimistically toggle the reaction in the unified-feed cache so the
        // counter updates without refetching every page of the infinite query.
        const postId = post.id;
        rawQueryClient.setQueriesData({ queryKey: [["feed", "unified"]] }, (old: unknown) => {
            if (!old || typeof old !== "object") return old;
            const data = old as {
                pages: Array<{
                    items: Array<{
                        type: string;
                        post?: {
                            id: number;
                            reactions: Record<
                                string,
                                { count: number; reacted: boolean } | undefined
                            >;
                        };
                    }>;
                }>;
                pageParams: unknown[];
            };
            return {
                ...data,
                pages: data.pages.map((page) => ({
                    ...page,
                    items: page.items.map((item) => {
                        if (item.type !== "newPost" || item.post?.id !== postId) return item;
                        const current = item.post.reactions[reaction] ?? {
                            count: 0,
                            reacted: false,
                        };
                        const nextReacted = !current.reacted;
                        const nextCount = Math.max(0, current.count + (nextReacted ? 1 : -1));
                        return {
                            ...item,
                            post: {
                                ...item.post,
                                reactions: {
                                    ...item.post.reactions,
                                    [reaction]: {
                                        count: nextCount,
                                        reacted: nextReacted,
                                    },
                                },
                            },
                        };
                    }),
                })),
            };
        });

        togglePostReactionMutation.mutate(
            { postId, reaction },
            {
                onSuccess: () => {
                    queryClient.social.posts.get.invalidate({ postId });
                },
            },
        );
    };

    return (
        <Reactions
            reactions={post.reactions}
            commentsCount={post.comments.length}
            toggleReaction={toggleReaction}
            isCurrentUser={Boolean(currentUser?.id)}
            isPost
        />
    );
};
